import React, {useEffect, useRef, useState} from 'react';
import {Table, Spinner, Button} from 'react-bootstrap';
import {deleteMotor} from 'api/MotorsApi';
import ModalDialog from 'components/Dialogs/ModalDialog';
import {useAppContext} from 'contexts/AppContext';
import {isSystemAdmin} from 'utils/Auth';
import {isEmpty} from 'utils/Utils';

export default function MotorsList({
    pageState, currentVersion, currentConfiguration, onDataUpdate, onDetailsClick, onAddButtonClick
}) {
    const isMounted = useRef(false);
    const motorObject = useRef(null);
    const {showMessage, handleApiError} = useAppContext();
    const [isLoading, setIsLoading] = useState(true);
    const [deleteDialogBody, setDeleteDialogBody] = useState(<></>);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [motors, setMotors] = useState([]);
    const isEditable = isSystemAdmin() && currentVersion.status === 0;

    useEffect(() => {
        isMounted.current = true;
        getMotors();
        return () => isMounted.current = false;
    }, [pageState, currentVersion, currentConfiguration]);

    const getMotors = () => {
        if (isMounted.current) {
            setIsLoading(true);
            if (!isEmpty(currentConfiguration) && !isEmpty(currentConfiguration.crane_details)) {
                setMotors(currentConfiguration.crane_details.motor_config);
            }
            setIsLoading(false);
        }
    };

    const handleDeleteClick = motor => {
        motorObject.current = motor;
        setDeleteDialogBody(<p>Do you really want to delete the motor "<b>{motor.name}</b>"?</p>);
        setShowDeleteDialog(true);
    };

    const handleModalClosed = () => {
        setShowDeleteDialog(false);
    };

    const handleModalConfirmed = () => {
        setIsLoading(true);
        const onDone = () => isMounted.current && setIsLoading(false);
        deleteMotor(pageState.craneId, motorObject.current.id, handleSuccessResponse, handleApiError, onDone);
        handleModalClosed();
    };

    const handleSuccessResponse = (response) => {
        if (isMounted.current) {
            showMessage('success', 'Motor configuration has been successfully saved');
            onDataUpdate();
        }
    };

    let addButton;
    if (isEditable) addButton = <button className="btn btn-add" onClick={onAddButtonClick}>Add</button>;

    let tableContent;
    if (isLoading) tableContent = <tr><td colSpan={isEditable ? 7 : 6} className="text-center"><Spinner animation="border" /></td></tr>;
    else if (motors.length === 0) tableContent = <tr><td colSpan={isEditable ? 7 : 6}>No motors found</td></tr>;
    else tableContent = motors.map(motor => (
        <tr key={motor.id}>
            <td>{motor.name}</td>
            <td>{motor.vfd_name}</td>
            <td className="text-center">{motor.horsepower}</td>
            <td className="text-center">{motor.motor_amps.rated}</td>
            <td className="text-center">{motor.motor_reduction_factor}</td>
            <td className="text-center">
                <span className="text-primary text-link" onClick={() => onDetailsClick(motor)}>{isEditable ? 'Edit' : 'View'}</span>
            </td>
            {isEditable &&
                <td className="text-center">
                    <span className="text-primary text-link" onClick={() => handleDeleteClick(motor)}>Delete</span>
                </td>}
        </tr>
    ));

    return (
        <>
            <ModalDialog title="Deleting motor" body={deleteDialogBody} show={showDeleteDialog} onHide={handleModalClosed}>
                <Button variant="secondary" onClick={handleModalClosed}>No</Button>
                <Button variant="danger" onClick={handleModalConfirmed}>Yes</Button>
            </ModalDialog>
            <Table striped bordered hover size="sm">
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>VFD</th>
                        <th className="text-center">Horsepower</th>
                        <th className="text-center">Current (Amps) rated</th>
                        <th className="text-center">Motor / motion speed reduction factor</th>
                        <th colSpan={isEditable ? 2 : 1} className="text-center">{addButton}</th>
                    </tr>
                </thead>
                <tbody>{tableContent}</tbody>
            </Table>
        </>
    );
}
